import { useState } from "react";
import { Box } from "lucide-react";
import { api, type Model } from "../lib/api";

type Props = {
  model: Pick<Model, "id" | "name" | "preview_key" | "preview_updated_at">;
  className?: string;
  /** Render a smaller fallback icon — useful in tight rows. */
  compact?: boolean;
};

/**
 * Renders the model's preview PNG if one has been captured, otherwise a
 * stylized gradient fallback with the model's first letter so the admin grid
 * never has an empty visual slot.
 */
export default function Thumbnail({ model, className = "", compact = false }: Props) {
  const url = api.modelPreviewUrl(model);
  const [errored, setErrored] = useState(false);
  const showImage = url && !errored;

  return (
    <div
      className={
        "relative flex items-center justify-center overflow-hidden " +
        "bg-[#eef2ef] " +
        className
      }
    >
      {showImage ? (
        <img
          src={url!}
          alt={`${model.name} preview`}
          className="h-full w-full object-cover"
          loading="lazy"
          onError={() => setErrored(true)}
        />
      ) : (
        <Fallback name={model.name} compact={compact} />
      )}
    </div>
  );
}

function Fallback({ name, compact }: { name: string; compact: boolean }) {
  const letter = (name?.trim()?.[0] ?? "?").toUpperCase();
  return (
    <div className="relative flex h-full w-full items-center justify-center">

      {compact ? (
        <Box size={20} className="text-brand-500" />
      ) : (
        <div className="relative flex flex-col items-center text-brand-600/70">
          <div className="font-serif text-6xl tracking-tight">{letter}</div>
          <div className="mt-1 text-[10px] uppercase tracking-[0.18em] text-slate-500">
            3D anatomical scan
          </div>
        </div>
      )}
    </div>
  );
}
